import { GameConsole } from "../console/gameConsole";

/**
 * All War Look sounds are synthesised with Web Audio (no files to license or download):
 *  - barrage(): a rolling artillery barrage when the gas starts to advance
 *  - distantShelling(): an occasional far-off rumble (the screen glows on that side)
 *  - setBreathing(): slow gas-mask breathing while you are inside the gas
 * and the skies' (1.05, war/warSky.ts), whatever the graphics level:
 *  - thunder(): a thunderstorm's thunder, its crack and its long roll (later the farther the lightning)
 *  - setRain() / setWind(): the storm's rain and the snow's wind, as long as the match lasts
 *  - flashlightClick(): your flashlight switched off or on, at night
 */
class WarAudioClass {
    private _ctx?: AudioContext;
    private _brown?: AudioBuffer;
    private _white?: AudioBuffer;
    private _echo?: { input: GainNode };
    private _breath?: { gain: GainNode, source: AudioBufferSourceNode, lfo: OscillatorNode };
    private _breathing = false;
    /** the skies' long sounds: the storm's rain, the snow's wind (built when first wanted, kept, faded in and out) */
    private _rain?: { readonly gain: GainNode, readonly sources: readonly AudioScheduledSourceNode[] };
    private _wind?: { readonly gain: GainNode, readonly sources: readonly AudioScheduledSourceNode[] };
    /**
     * everything goes out through this, at the sound volume as it is now (it follows the settings: the rain set once at
     * the map's level kept it when the volume changed, and muted at the start there was none at all: 1.06.1's audit)
     */
    private _master?: GainNode;
    private _listening = false;

    constructor() {
        const unlock = (): void => { void this._ensure()?.resume(); };
        window.addEventListener("pointerdown", unlock, { passive: true });
        window.addEventListener("keydown", unlock, { passive: true });
    }

    private _volume(): number {
        try {
            return Number(GameConsole.getBuiltInCVar("cv_master_volume")) * Number(GameConsole.getBuiltInCVar("cv_sfx_volume"));
        } catch { return 0.6; }
    }

    private _ensure(): AudioContext | undefined {
        try {
            if (!this._ctx) {
                const ctx = this._ctx = new AudioContext();
                const rate = ctx.sampleRate;
                // brown noise: the rolling body of an explosion heard from far away
                const brown = this._brown = ctx.createBuffer(1, rate * 3, rate);
                const b = brown.getChannelData(0);
                let last = 0, peak = 0;
                for (let i = 0; i < b.length; i++) {
                    last = (last + 0.02 * (Math.random() * 2 - 1)) / 1.02;
                    b[i] = last; peak = Math.max(peak, Math.abs(last));
                }
                for (let i = 0; i < b.length; i++) b[i] /= peak;
                const white = this._white = ctx.createBuffer(1, rate * 2, rate);
                const w = white.getChannelData(0);
                for (let i = 0; i < w.length; i++) w[i] = Math.random() * 2 - 1;
                this._outputs(ctx);
                this._listen();
            }
            return this._ctx;
        } catch { return undefined; }
    }

    /** the master level (the sound volume) and, into it, the valley echo every thump shares */
    private _outputs(ctx: AudioContext): void {
        const master = this._master = ctx.createGain();
        master.gain.value = this._volume();
        master.connect(ctx.destination);
        // a valley echo shared by every thump: short delay with a darkened feedback loop
        const input = ctx.createGain();
        const delay = ctx.createDelay(1); delay.delayTime.value = 0.23;
        const feedback = ctx.createGain(); feedback.gain.value = 0.32;
        const tone = ctx.createBiquadFilter(); tone.type = "lowpass"; tone.frequency.value = 420;
        input.connect(delay); delay.connect(tone); tone.connect(feedback); feedback.connect(delay);
        tone.connect(master);
        this._echo = { input };
    }

    /** the master level follows the volume settings as they're changed */
    private _listen(): void {
        if (this._listening) return;
        this._listening = true;
        const follow = (): void => {
            const ctx = this._ctx, master = this._master;
            if (!ctx || !master) return;
            master.gain.cancelScheduledValues(ctx.currentTime);
            master.gain.setTargetAtTime(this._volume(), ctx.currentTime, 0.05);
        };
        try {
            GameConsole.variables.addChangeListener("cv_master_volume", follow);
            GameConsole.variables.addChangeListener("cv_sfx_volume", follow);
        } catch { /* (no settings yet: the level stays as it was made) */ }
    }

    /**
     * everything still playing, or still to play, cut: the rain, the wind, the breathing, a thunder's roll or a barrage
     * still on its way (a match left, its connection gone: they went on over the end and the menu, 1.06.1's audit)
     */
    hush(): void {
        const ctx = this._ctx;
        if (!ctx) return;
        try { this._master?.disconnect(); } catch { /* already */ }
        for (const bed of [this._rain, this._wind]) {
            for (const source of bed?.sources ?? []) {
                try { source.stop(); } catch { /* not started */ }
            }
        }
        if (this._breath) {
            try { this._breath.source.stop(); this._breath.lfo.stop(); } catch { /* not started */ }
        }
        this._rain = this._wind = this._breath = undefined;
        this._breathing = false;
        this._outputs(ctx);
    }

    /** where the sounds go: the master level */
    private _out(ctx: AudioContext): AudioNode {
        return this._master ?? ctx.destination;
    }

    /** one distant detonation: low-passed noise plus a falling sine "body" */
    private _thump(at: number, gain: number, pan: number, cutoff: number, decay: number): void {
        const ctx = this._ctx, brown = this._brown, echo = this._echo;
        if (!ctx || !brown || !echo) return;
        const t = ctx.currentTime + at;
        const out = ctx.createGain();
        const panner = ctx.createStereoPanner(); panner.pan.value = pan;
        out.connect(panner); panner.connect(this._out(ctx));
        const send = ctx.createGain(); send.gain.value = 0.45; out.connect(send); send.connect(echo.input);

        const noise = ctx.createBufferSource(); noise.buffer = brown;
        noise.playbackRate.value = 0.85 + Math.random() * 0.3;
        const low = ctx.createBiquadFilter(); low.type = "lowpass"; low.frequency.value = cutoff; low.Q.value = 0.7;
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.0001, t);
        env.gain.exponentialRampToValueAtTime(gain, t + 0.035);
        env.gain.exponentialRampToValueAtTime(0.0001, t + decay);
        noise.connect(low); low.connect(env); env.connect(out);
        noise.start(t, Math.random() * 1.5, decay + 0.1);

        const body = ctx.createOscillator(); body.type = "sine";
        body.frequency.setValueAtTime(58 * (0.9 + Math.random() * 0.2), t);
        body.frequency.exponentialRampToValueAtTime(27, t + 0.7);
        const bodyEnv = ctx.createGain();
        bodyEnv.gain.setValueAtTime(0.0001, t);
        bodyEnv.gain.exponentialRampToValueAtTime(gain * 0.8, t + 0.02);
        bodyEnv.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
        body.connect(bodyEnv); bodyEnv.connect(out);
        body.start(t); body.stop(t + 1);
    }

    /** the gas starts to move: a rolling barrage */
    barrage(): void {
        const volume = this._volume();
        if (!(volume > 0) || !this._ensure()) return;
        const count = 5 + Math.floor(Math.random() * 3);
        let at = 0.05;
        for (let i = 0; i < count; i++) {
            this._thump(at, 0.5 * (0.75 + Math.random() * 0.25), (Math.random() * 2 - 1) * 0.65, 110 + Math.random() * 70, 2.6 + Math.random() * 1.4);
            at += 0.3 + Math.random() * 0.65;
        }
    }

    /** far away, occasional; `pan` follows the glow on screen */
    distantShelling(pan: number): void {
        const volume = this._volume();
        if (!(volume > 0) || !this._ensure()) return;
        const count = 1 + Math.floor(Math.random() * 3);
        let at = 0.02;
        for (let i = 0; i < count; i++) {
            this._thump(at, 0.2, Math.max(-0.9, Math.min(0.9, pan * 0.8 + (Math.random() - 0.5) * 0.2)), 75 + Math.random() * 40, 3 + Math.random() * 1.2);
            at += 0.45 + Math.random() * 0.9;
        }
    }

    /**
     * thunder: `at` seconds from now (the lightning's distance: sound is slower than light), `strength` 0 to 1 (a close
     * strike cracks, a far one only rolls), `pan` the side it's on
     */
    thunder(at: number, strength: number, pan: number): void {
        const volume = this._volume();
        const ctx = this._ensure();
        if (!(volume > 0) || !ctx || !this._brown || !this._white || !this._echo) return;
        const t = ctx.currentTime + Math.max(0, at);
        const out = ctx.createGain();
        const panner = ctx.createStereoPanner(); panner.pan.value = Math.max(-0.9, Math.min(0.9, pan));
        out.connect(panner); panner.connect(this._out(ctx));
        const send = ctx.createGain(); send.gain.value = 0.5; out.connect(send); send.connect(this._echo.input);
        const level = 0.25 + 0.4 * strength;
        // the crack, close by: a tearing burst of high noise
        if (strength > 0.45) {
            const crack = ctx.createBufferSource(); crack.buffer = this._white;
            const high = ctx.createBiquadFilter(); high.type = "highpass"; high.frequency.value = 900;
            const env = ctx.createGain();
            env.gain.setValueAtTime(0.0001, t);
            env.gain.exponentialRampToValueAtTime(level * 0.9 * strength, t + 0.012);
            env.gain.exponentialRampToValueAtTime(0.0001, t + 0.35 + 0.25 * strength);
            crack.connect(high); high.connect(env); env.connect(out);
            crack.start(t, Math.random(), 0.8);
        }
        // the roll: low noise swelling and fading a few times over seconds
        const roll = ctx.createBufferSource(); roll.buffer = this._brown; roll.loop = true;
        roll.playbackRate.value = 0.7 + Math.random() * 0.25;
        const low = ctx.createBiquadFilter(); low.type = "lowpass"; low.frequency.value = 160 + 260 * strength; low.Q.value = 0.6;
        const env = ctx.createGain();
        const length = 3.2 + 2.5 * Math.random() + 1.5 * strength;
        env.gain.setValueAtTime(0.0001, t);
        env.gain.exponentialRampToValueAtTime(level, t + 0.06 + 0.25 * (1 - strength));
        // (its swells spread over its length, in order, each ramp after the one before)
        [0.18, 0.38, 0.6].forEach((share, swell) => {
            const dip = t + length * (share + (Math.random() - 0.5) * 0.06);
            env.gain.exponentialRampToValueAtTime(level * (0.35 + Math.random() * 0.4), dip);
            env.gain.exponentialRampToValueAtTime(level * (0.6 + Math.random() * 0.35) * (1 - swell * 0.22), dip + length * 0.07);
        });
        env.gain.exponentialRampToValueAtTime(0.0001, t + length);
        roll.connect(low); low.connect(env); env.connect(out);
        roll.start(t, Math.random() * 1.5);
        roll.stop(t + length + 0.1);
    }

    /** a long noise bed for the skies (rain or wind), its level faded to `level` (the master level is the sound volume) */
    private _bed(which: "rain" | "wind", level: number): void {
        const ctx = level > 0 ? this._ensure() : this._ctx;
        if (!ctx || !this._white || !this._brown) return;
        let bed = which === "rain" ? this._rain : this._wind;
        if (!bed && level > 0) {
            const gain = ctx.createGain(); gain.gain.value = 0;
            const sources: AudioScheduledSourceNode[] = [];
            if (which === "rain") {
                // the hiss of the drops, and the roar under it
                const hiss = ctx.createBufferSource(); hiss.buffer = this._white; hiss.loop = true;
                const hp = ctx.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = 500;
                const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 5200;
                const roar = ctx.createBufferSource(); roar.buffer = this._brown; roar.loop = true;
                const roarLp = ctx.createBiquadFilter(); roarLp.type = "lowpass"; roarLp.frequency.value = 700;
                const roarGain = ctx.createGain(); roarGain.gain.value = 0.9;
                hiss.connect(hp); hp.connect(lp); lp.connect(gain);
                roar.connect(roarLp); roarLp.connect(roarGain); roarGain.connect(gain);
                hiss.start(); roar.start();
                sources.push(hiss, roar);
            } else {
                // the wind: low noise, its gusts rising and falling slowly
                const noise = ctx.createBufferSource(); noise.buffer = this._brown; noise.loop = true;
                const band = ctx.createBiquadFilter(); band.type = "bandpass"; band.frequency.value = 380; band.Q.value = 0.5;
                const gust = ctx.createGain(); gust.gain.value = 0.6;
                const lfo = ctx.createOscillator(); lfo.frequency.value = 0.08;
                const depth = ctx.createGain(); depth.gain.value = 0.4;
                lfo.connect(depth); depth.connect(gust.gain);
                noise.connect(band); band.connect(gust); gust.connect(gain);
                noise.start(); lfo.start();
                sources.push(noise, lfo);
            }
            gain.connect(this._out(ctx));
            bed = { gain, sources };
            if (which === "rain") this._rain = bed;
            else this._wind = bed;
        }
        if (!bed) return;
        const target = level > 0 ? level : 0;
        bed.gain.gain.cancelScheduledValues(ctx.currentTime);
        bed.gain.gain.setTargetAtTime(target, ctx.currentTime, level > 0 ? 1.2 : 0.4);
    }

    /** the storm's rain, `level` 0 (none) to 1 */
    setRain(level: number): void { this._bed("rain", 0.07 * level); }

    /** the snow's wind, `level` 0 (none) to 1 */
    setWind(level: number): void { this._bed("wind", 0.05 * level); }

    /** your flashlight switched: a small click */
    flashlightClick(): void {
        const volume = this._volume();
        const ctx = this._ensure();
        if (!(volume > 0) || !ctx || !this._white) return;
        const t = ctx.currentTime;
        const click = ctx.createBufferSource(); click.buffer = this._white;
        const band = ctx.createBiquadFilter(); band.type = "bandpass"; band.frequency.value = 2600; band.Q.value = 2.5;
        const env = ctx.createGain();
        env.gain.setValueAtTime(0.0001, t);
        env.gain.exponentialRampToValueAtTime(0.5, t + 0.002);
        env.gain.exponentialRampToValueAtTime(0.0001, t + 0.03);
        click.connect(band); band.connect(env); env.connect(this._out(ctx));
        click.start(t, Math.random(), 0.05);
        const body = ctx.createOscillator(); body.type = "sine"; body.frequency.setValueAtTime(240, t);
        body.frequency.exponentialRampToValueAtTime(120, t + 0.04);
        const bodyEnv = ctx.createGain();
        bodyEnv.gain.setValueAtTime(0.0001, t);
        bodyEnv.gain.exponentialRampToValueAtTime(0.18, t + 0.003);
        bodyEnv.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
        body.connect(bodyEnv); bodyEnv.connect(this._out(ctx));
        body.start(t); body.stop(t + 0.06);
    }

    /** slow breathing through a mask filter, while inside the gas */
    setBreathing(on: boolean): void {
        if (on === this._breathing) return;
        this._breathing = on;
        const ctx = on ? this._ensure() : this._ctx;
        if (!ctx) return;
        if (on && !this._breath && this._white) {
            const source = ctx.createBufferSource(); source.buffer = this._white; source.loop = true;
            const band = ctx.createBiquadFilter(); band.type = "bandpass"; band.frequency.value = 620; band.Q.value = 1.4;
            const gain = ctx.createGain(); gain.gain.value = 0;
            // breathing rhythm: a slow LFO opening and closing the level (inhale, exhale)
            const lfo = ctx.createOscillator(); lfo.frequency.value = 0.27;
            const depth = ctx.createGain(); depth.gain.value = 0.5;
            const shaped = ctx.createGain(); shaped.gain.value = 0;
            lfo.connect(depth); depth.connect(shaped.gain);
            source.connect(band); band.connect(shaped); shaped.connect(gain); gain.connect(this._out(ctx));
            source.start(); lfo.start();
            this._breath = { gain, source, lfo };
        }
        const breath = this._breath;
        if (!breath) return;
        const target = on ? 0.09 : 0;
        breath.gain.gain.cancelScheduledValues(ctx.currentTime);
        breath.gain.gain.setTargetAtTime(target, ctx.currentTime, on ? 0.35 : 0.2);
    }
}

export const WarAudio = new WarAudioClass();
